import { type AxiosError } from 'axios'

import {
  HttpHeaders,
  type HttpClientConfig,
  type HttpClientInterface,
} from 'core/infra/http/HttpClient'

import { type BulkRates } from '../domain/BulkRates'
import { type BookNowStatus } from '../domain/Rate'
import { type Rates } from '../domain/Rates'
import { type RawBulkRates, mapApiToBulkRates } from './BulkRatesMapper'
import { type SubmitRateParams, mapRateToApi } from './RateMapper'
import { type RawBookNowStatus, mapApiToBookNowStatus } from './RateMapper'
import { mapApiToRates, mapApiToRefreshTokenData, type RawRates } from './RatesMapper'

type RateCommandCenterRepositoryDependencies = {
  httpClient: HttpClientInterface
}

const BASE_URL = '/carrier/rates'

type Params = {
  token: string
  offset: number
  limit: number
  status: string
  config?: Partial<HttpClientConfig>
}

export type BookNowResult = {
  uuid: string
}

export type ResponseError = AxiosError<{ detail: string }> | undefined

export function isTokenExpiredError(error: ResponseError) {
  if (!error) {
    return false
  }

  return error.response?.data.detail === 'token expired'
}

export class RateCommandCenterRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient }: RateCommandCenterRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getPaginatedRatesByStatus({
    token,
    offset,
    limit,
    status,
    config,
  }: Params): Promise<Rates> {
    const headers = {
      Authorization: `Bearer ${token}`,
    }

    const response = await this.httpClient.get<RawRates>(
      `${BASE_URL}?limit=${limit}&offset=${offset}&status=${status}`,
      {
        ...config,
        headers: new HttpHeaders({
          ...headers,
        }),
      }
    )

    return mapApiToRates(response.data)
  }

  async submitRate({
    token,
    rate,
    config,
  }: {
    token: string
    rate: SubmitRateParams
    config?: Partial<HttpClientConfig>
  }) {
    const headers = {
      Authorization: `Bearer ${token}`,
    }
    const endpoint = `${BASE_URL}/${rate.id}/submit`

    await this.httpClient.patch(endpoint, mapRateToApi(rate), {
      ...config,
      headers: new HttpHeaders({
        ...config?.headers,
        ...headers,
      }),
    })
  }

  async rejectRate({
    token,
    rateId,
    reason,
    config,
  }: {
    token: string
    rateId: string
    reason: string
    config?: Partial<HttpClientConfig>
  }) {
    const headers = {
      Authorization: `Bearer ${token}`,
    }
    const endpoint = `${BASE_URL}/${rateId}/reject`
    const data = { reject_reason: reason }
    await this.httpClient.patch(endpoint, data, {
      ...config,
      headers: new HttpHeaders({
        ...config?.headers,
        ...headers,
      }),
    })
  }

  async bookNowRate({
    token,
    rate,
    config,
  }: {
    token: string
    rate: SubmitRateParams
    config?: Partial<HttpClientConfig>
  }): Promise<BookNowResult> {
    const endpoint = `${BASE_URL}/${rate.id}/book-now`

    const response = await this.httpClient.patch<BookNowResult>(endpoint, mapRateToApi(rate), {
      ...config,
      headers: new HttpHeaders({
        ...(config?.headers || {}),
        Authorization: `Bearer ${token}`,
      }),
    })

    return response.data
  }

  async getBookNowStatus({
    token,
    uuid,
    config,
  }: {
    token: string
    uuid: string
    config?: Partial<HttpClientConfig>
  }): Promise<BookNowStatus> {
    const endpoint = `/tenders/book-quote-now-action/${uuid}`

    const response = await this.httpClient.get<RawBookNowStatus>(endpoint, {
      ...config,
      headers: new HttpHeaders({
        ...(config?.headers || {}),
        Authorization: `Bearer ${token}`,
      }),
    })

    try {
      return mapApiToBookNowStatus(response.data)
    } catch (error) {
      throw new Error('Failed to map API return to BookNowStatus', { cause: error as Error })
    }
  }

  async refreshToken(
    token: string,
    data?: { rate_uuid?: string; bulk_invite_uuid?: string },
    config?: Partial<HttpClientConfig>
  ): Promise<string | undefined> {
    const headers = {
      Authorization: `Bearer ${token}`,
    }

    const response = await this.httpClient.post(`carriers/token/refresh`, data, {
      ...config,
      headers: new HttpHeaders({
        ...config?.headers,
        ...headers,
      }),
    })

    return mapApiToRefreshTokenData(response.data)
  }

  async getBulkRatesById(id: string, config?: Partial<HttpClientConfig>): Promise<BulkRates> {
    const endpoint = `/bulk/bulk-invite/carrier/${id}`

    const response = await this.httpClient.get<RawBulkRates>(endpoint, config)

    try {
      return mapApiToBulkRates(response.data)
    } catch (error) {
      throw new Error('Failed to map API return to BulkRates', { cause: error as Error })
    }
  }
}
